/**
 * Central timezone utilities for consistent time handling across the app
 * This ensures all times are saved and displayed correctly regardless of timezone
 */

/**
 * Converts a date string (YYYY-MM-DD) and time string (HH:MM AM/PM) to local ISO string
 * This ensures the time is saved as the user intended in their local timezone
 */
export const createUTCDateTime = (dateString: string, timeString: string): string => {
  // Parse the date components
  const [year, month, day] = dateString.split('-').map(Number);
  
  // Convert time to 24-hour format
  const time24 = convertTimeTo24Hour(timeString);
  const [hour, minute] = time24.split(':').map(Number);
  
  // Create date in local timezone (not UTC) to preserve user's intended time
  const localDate = new Date(year, month - 1, day, hour, minute, 0, 0);
  
  return localDate.toISOString();
};

/**
 * Converts a UTC ISO string back to local time for display
 * This ensures times are displayed in the user's local timezone
 */
export const formatDateTimeForDisplay = (utcIsoString: string): { date: string; time: string } => {
  const date = new Date(utcIsoString);
  
  // Format date as YYYY-MM-DD
  const dateString = date.getFullYear() + '-' + 
    String(date.getMonth() + 1).padStart(2, '0') + '-' + 
    String(date.getDate()).padStart(2, '0');
  
  // Format time as 12-hour format
  const timeString = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  
  return { date: dateString, time: timeString };
};

/**
 * Converts 12-hour time format to 24-hour format
 * Used internally by createUTCDateTime
 */
function convertTimeTo24Hour(time12: string): string {
  // Handle empty or invalid input
  if (!time12 || typeof time12 !== 'string' || time12.trim() === '') {
    throw new Error('Invalid time format: empty or null value');
  }
  
  const trimmed = time12.trim();
  const parts = trimmed.split(' ');
  
  // Must have at least time part
  if (parts.length === 0) {
    throw new Error(`Invalid time format: "${time12}"`);
  }
  
  const time = parts[0];
  const period = parts[1]?.toUpperCase() || '';
  
  // Validate time format (HH:MM, H:MM, HH:M, or H:M) - more flexible to handle partial input during typing
  const timeMatch = time.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!timeMatch) {
    throw new Error(`Invalid time format: "${time12}" - expected format "HH:MM AM/PM"`);
  }
  
  let hours = parseInt(timeMatch[1], 10);
  let minutes = parseInt(timeMatch[2], 10);
  
  // Validate hours and minutes
  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 12 || minutes < 0 || minutes > 59) {
    throw new Error(`Invalid time values: "${time12}"`);
  }
  
  // Convert to 24-hour format
  if (period === 'AM') {
    if (hours === 12) {
      hours = 0;
    }
  } else if (period === 'PM') {
    if (hours !== 12) {
      hours += 12;
    }
  } else if (period !== '') {
    // If period is provided but not AM/PM, it's invalid
    throw new Error(`Invalid time period: "${period}" - expected AM or PM`);
  }
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Gets the current date in YYYY-MM-DD format (local timezone)
 * Used for initializing date inputs
 * @param date - Optional date to format (defaults to now)
 */
export const getCurrentDateString = (date?: Date): string => {
  const d = date || new Date();
  return d.getFullYear() + '-' + 
    String(d.getMonth() + 1).padStart(2, '0') + '-' + 
    String(d.getDate()).padStart(2, '0');
};

/**
 * Validates if a date/time combination is in the past
 * Used for preventing scheduling classes in the past
 */
export const isDateTimeInPast = (dateString: string, timeString: string): boolean => {
  const utcDateTime = createUTCDateTime(dateString, timeString);
  const now = new Date();
  return new Date(utcDateTime) < now;
};

/**
 * Gets the user's timezone from the browser
 * This is timezone-agnostic and works for users anywhere in the world
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Gets the user's preferred timezone from their profile, with fallback to browser timezone
 * This ensures consistency across devices while allowing users to override their timezone preference
 * @param userId - User ID to fetch timezone from profile
 * @returns Promise<string> - User's preferred timezone or browser timezone as fallback
 */
export const getUserTimezoneFromProfile = async (userId: string | undefined): Promise<string> => {
  if (!userId) {
    return getUserTimezone(); // Fallback to browser timezone if no user
  }

  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('timezone')
      .eq('id', userId)
      .single();

    if (error) {
      console.warn('[getUserTimezoneFromProfile] Error fetching profile timezone:', error);
      return getUserTimezone(); // Fallback to browser timezone on error
    }

    // Use profile timezone if available, otherwise fallback to browser timezone
    return profile?.timezone || getUserTimezone();
  } catch (error) {
    console.warn('[getUserTimezoneFromProfile] Error:', error);
    return getUserTimezone(); // Fallback to browser timezone on error
  }
};

/**
 * Extracts date in user's local timezone from a UTC timestamp
 * This ensures dates match what users see in the UI based on their browser timezone
 * The Date object automatically converts UTC to local timezone when using getFullYear(), getMonth(), getDate()
 * @param timestamp - UTC timestamp string (ISO format)
 * @returns Date string in YYYY-MM-DD format in user's local timezone
 */
export const extractDateInUserTimezone = (timestamp: string): string => {
  // Create date from UTC timestamp
  // When we use getFullYear(), getMonth(), getDate(), JavaScript automatically
  // converts to the user's local timezone (browser/system timezone)
  const date = new Date(timestamp);
  
  // Extract date components in user's local timezone
  // These methods automatically use the browser's timezone
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  // Format as YYYY-MM-DD
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
